import api from './api';
import type { StockCount, StockCountLine, PaginatedResponse, StockCountStatus } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/stock-count`;

export interface CreateCountPayload {
  locationId?: string | null;
  countedAt?: string | null;
  countedBy?: string;
  notes?: string;
}

export interface UpdateCountPayload {
  countedAt?: string | null;
  countedBy?: string;
  notes?: string;
}

export interface PostCountResult {
  count: StockCount;
  movementsPosted: number;
}

export interface BulkLoadResult {
  added: number;
  lines: StockCountLine[];
}

const stockCountService = {
  getCounts: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    status?: StockCountStatus,
  ): Promise<PaginatedResponse<StockCount>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (status) params.status = status;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  getCount: async (
    venueId: string,
    countId: string,
  ): Promise<{ count: StockCount; lines: StockCountLine[] }> => {
    const response = await api.get(`${base(venueId)}/${countId}`);
    return response.data;
  },

  /** Create an empty draft count. Lines are not auto-populated. */
  createCount: async (
    venueId: string,
    data: CreateCountPayload,
  ): Promise<StockCount> => {
    const response = await api.post(base(venueId), data);
    return response.data;
  },

  /** Update header fields (countedAt, countedBy, notes). Draft only. */
  updateCount: async (
    venueId: string,
    countId: string,
    data: UpdateCountPayload,
  ): Promise<StockCount> => {
    const response = await api.patch(`${base(venueId)}/${countId}`, data);
    return response.data;
  },

  /**
   * Add a single line by inventoryItemId.
   * Freezes expectedQty from InventoryBalance at this moment.
   * Draft only. Line scope must match count scope.
   */
  addLine: async (
    venueId: string,
    countId: string,
    inventoryItemId: string,
  ): Promise<StockCountLine> => {
    const response = await api.post(`${base(venueId)}/${countId}/lines`, { inventoryItemId });
    return response.data;
  },

  /**
   * Bulk-add all InventoryItems in count scope not already on the count.
   * Each line's expectedQty is frozen at the time of this call.
   * Draft only.
   */
  bulkLoadLines: async (
    venueId: string,
    countId: string,
  ): Promise<BulkLoadResult> => {
    const response = await api.post(`${base(venueId)}/${countId}/lines/bulk-load`);
    return response.data;
  },

  /** Update countedQty for a single line. Pass null to clear. Draft only. */
  updateLine: async (
    venueId: string,
    countId: string,
    lineId: string,
    countedQty: number | null,
  ): Promise<StockCountLine> => {
    const response = await api.patch(`${base(venueId)}/${countId}/lines/${lineId}`, { countedQty });
    return response.data;
  },

  /** Remove a line from the count. Draft only. */
  removeLine: async (
    venueId: string,
    countId: string,
    lineId: string,
  ): Promise<void> => {
    await api.delete(`${base(venueId)}/${countId}/lines/${lineId}`);
  },

  /**
   * Post the count.
   * Creates COUNT_VARIANCE InventoryMovements for all lines where varianceQty !== 0.
   * Zero-variance and null-countedQty lines are skipped.
   * Updates InventoryBalance via $inc. Atomic (MongoDB transaction).
   * One-way action — count becomes immutable after this.
   */
  postCount: async (
    venueId: string,
    countId: string,
  ): Promise<PostCountResult> => {
    const response = await api.post(`${base(venueId)}/${countId}/post`);
    return response.data;
  },

  /** Delete a draft count. Not allowed after posting. */
  deleteCount: async (venueId: string, countId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${countId}`);
  },
};

export default stockCountService;
